import jwtAuthKioskAxios from "../auth/jwtAuthKiosk";
import { setBackendDomain } from "./management";

// AdminUI client for the backend QR admin API (/api/v1/qr/*). customerId is derived from the JWT on
// the backend, so customer-bound calls (slug, tables, orders…) MUST carry the EDITED customer's token
// — pass `accessToken` (minted via management.generateToken(customerId)). Without it the ambient
// kiosk token is used (the logged-in / logged-in-as customer).
const qr = () => {
  setBackendDomain(jwtAuthKioskAxios);
  return jwtAuthKioskAxios;
};
const authCfg = (accessToken) =>
  accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {};

// --- settings + Besteron key encryption ---
export const getQrSettings = async (accessToken) => {
  const { data } = await qr().get(`/api/v1/qr/settings`, authCfg(accessToken));
  return data?.data;
};

// F2: list the EDITED customer's menus (code + title) for the DINE_IN/TAKEAWAY menu-assignment selects.
// GET /api/v1/menu returns { data: [menu…] }; each menu has `code` and a multilang `title`.
export const listCustomerMenus = async (accessToken) => {
  const { data } = await qr().get(`/api/v1/menu`, authCfg(accessToken));
  const list = (data && data.data) || [];
  return Array.isArray(list) ? list : [];
};

// Sends plaintext Besteron keys once; the backend returns ONLY the encrypted blob to store.
export const encryptBesteron = async (payload, accessToken) => {
  const { data } = await qr().put(`/api/v1/qr/besteron`, payload, authCfg(accessToken));
  return data?.data?.besteron;
};

// --- public URL slug (customer identifier in qrorder.datavision-online.eu/{slug}/...) ---
export const getQrSlug = async (accessToken) => {
  const { data } = await qr().get(`/api/v1/qr/slug`, authCfg(accessToken));
  return data?.data?.slug || null;
};

// Availability check without saving: returns { slug, available, reason }.
export const checkQrSlug = async (slug, accessToken) => {
  const { data } = await qr().get(`/api/v1/qr/slug/check?slug=${encodeURIComponent(slug)}`, authCfg(accessToken));
  return data?.data;
};

// Set/update this customer's slug. Throws on 4xx (slug_taken / slug_invalid_* / slug_reserved).
export const setQrSlug = async (slug, accessToken) => {
  const { data } = await qr().put(`/api/v1/qr/slug`, { slug }, authCfg(accessToken));
  return data?.data?.slug;
};

// --- F2 web ordering: custom domains (host → customer) ---
// { slug, domains:[{host,pathPrefix}] }
export const getWebDomains = async (accessToken) => {
  const { data } = await qr().get(`/api/v1/qr/web/domains`, authCfg(accessToken));
  return data?.data || { slug: null, domains: [] };
};
// Replace this customer's web domains. Throws on 4xx (domain_taken / domain_host_invalid / …).
export const setWebDomains = async (domains, accessToken) => {
  const { data } = await qr().put(`/api/v1/qr/web/domains`, { domains }, authCfg(accessToken));
  return data?.data?.domains || [];
};

// --- highlights (Tipy pre Vás / Odporúčame) + catalog pickers ---
// { tips, recommendations, languages, defaultLanguage, limits }
export const getQrHighlights = async (accessToken) => {
  const { data } = await qr().get(`/api/v1/qr/highlights`, authCfg(accessToken));
  return data?.data;
};

// Replace tips + recommendations. Body: { tips, recommendations }. Returns the stored shape.
export const setQrHighlights = async (body, accessToken) => {
  const { data } = await qr().put(`/api/v1/qr/highlights`, body, authCfg(accessToken));
  return data?.data;
};

// Catalog for the pickers: { categories:[{id,name,image}], products:[{id,name,image}] }. F2: `mode`
// (dine/take) selects the per-mode menu (Na mieste → DINE_IN, So sebou → TAKEAWAY) so the picker
// offers only items shown on the guest web for that mode; falls back to the "QR" menu server-side.
export const getQrCatalog = async (lang, accessToken, mode) => {
  const params = [];
  if (lang) params.push(`lang=${encodeURIComponent(lang)}`);
  if (mode) params.push(`mode=${encodeURIComponent(mode)}`);
  const q = params.length ? `?${params.join("&")}` : "";
  const { data } = await qr().get(`/api/v1/qr/catalog${q}`, authCfg(accessToken));
  return data?.data || { categories: [], products: [] };
};

// --- tables ---
export const listTables = async (accessToken) => {
  const { data } = await qr().get(`/api/v1/qr/tables`, authCfg(accessToken));
  return data?.data || [];
};

export const createTable = async (body, accessToken) => {
  const { data } = await qr().post(`/api/v1/qr/tables`, body, authCfg(accessToken));
  return data?.data;
};

export const updateTable = async (id, body, accessToken) => {
  const { data } = await qr().put(`/api/v1/qr/tables/${id}`, body, authCfg(accessToken));
  return data;
};

export const deleteTable = async (id, accessToken) => {
  const { data } = await qr().delete(`/api/v1/qr/tables/${id}`, authCfg(accessToken));
  return data;
};

export const rotateTableToken = async (id, accessToken) => {
  const { data } = await qr().post(`/api/v1/qr/tables/${id}/rotate-token`, undefined, authCfg(accessToken));
  return data?.data;
};

// QR as a data URL (bearer-protected JSON) so it can be shown inline via <img src=...>.
export const getTableQrDataUrl = async (id, accessToken) => {
  const { data } = await qr().get(`/api/v1/qr/tables/${id}/qr.json`, authCfg(accessToken));
  return data?.data; // { dataUrl, url }
};

// Live sticker preview (bearer-protected HTML) for the print settings picker → returns a blob URL to
// show in an iframe. Pass the current (possibly unsaved) print settings so the preview matches.
export const getQrStickerPreviewUrl = async (printSettings, accessToken) => {
  const res = await qr().post(`/api/v1/qr/tables/qr-sticker-preview`, printSettings || {}, {
    responseType: "blob",
    ...authCfg(accessToken),
  });
  return URL.createObjectURL(res.data);
};

// Printable sheet is bearer-protected HTML → fetch it, open as a blob in a new tab.
export const openTablesQrSheet = async (zone, accessToken) => {
  const client = qr();
  const res = await client.get(`/api/v1/qr/tables/qr-sheet${zone ? `?zone=${encodeURIComponent(zone)}` : ""}`, {
    responseType: "blob",
    ...authCfg(accessToken),
  });
  const blobUrl = URL.createObjectURL(res.data);
  window.open(blobUrl, "_blank");
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

// --- orders ---
export const listQrOrders = async (table, accessToken) => {
  const { data } = await qr().get(`/api/v1/qr/orders${table ? `?table=${encodeURIComponent(table)}` : ""}`, authCfg(accessToken));
  return data?.data || [];
};


// --- staff calls ---
export const listQrCalls = async (accessToken) => {
  const { data } = await qr().get(`/api/v1/qr/calls`, authCfg(accessToken));
  return data?.data || [];
};

export const markCallDone = async (id, doneBy, accessToken) => {
  const { data } = await qr().post(`/api/v1/qr/calls/${id}/done`, { doneBy }, authCfg(accessToken));
  return data;
};
